import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Clock-time x axis for the charts that plot a single day minute by minute.

// Room one label needs. Labels tilt to 45°, where overlap depends on line
// height rather than text width — budgeting for the full "00:30" costs about a
// third of the labels that actually fit.
const MINUTE_LABEL_PX = 20;

// Y-axis numbers plus the chart's left and right margins.
const PLOT_GUTTER_PX = 100;

const FALLBACK_MAX_LABELS = 24;

// Divides the visible span into as many labels as fit, rather than picking from
// a list of tidy steps: a day only divides tidily at 30 or 60 minutes, which
// leaves most of the axis empty. Steps above five still land on a five-minute
// boundary.
export function pickMinuteStep(span, width) {
  const maxLabels =
    width > 0 ? Math.max(6, Math.floor(width / MINUTE_LABEL_PX)) : FALLBACK_MAX_LABELS;

  const step = Math.ceil(span / maxLabels);
  if (step <= 1) return 1;
  if (step <= 5) return step;
  return Math.ceil(step / 5) * 5;
}

export function minorTicksFor(step) {
  if (step % 10 === 0) return Math.max(1, step / 10 - 1);
  if (step % 5 === 0) return Math.min(step / 5 - 1, 5);
  return step > 2 ? 1 : 0;
}

export function minuteLabel(minute) {
  const m = ((Math.round(minute) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

// Null for anything that is not a single day of minutes carrying its own
// minute index; those charts keep the axis they had.
export function minuteRangeOf(series, mode) {
  if (mode !== "minute" && mode !== "epg") return null;
  const list = Array.isArray(series) ? series : [];
  if (list.length === 0) return null;
  if (!list.every((p) => Number.isFinite(p?.index))) return null;
  return { min: list[0].index, max: list[list.length - 1].index };
}

// Every accepted width change re-renders the axis, so ignore small movements.
const WIDTH_EPSILON_PX = 16;

export function useElementWidth(ref) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const apply = (next) =>
      setWidth((prev) => (Math.abs(prev - next) < WIDTH_EPSILON_PX ? prev : next));

    // A chart that never resizes would otherwise stay on the fallback density.
    apply(el.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect?.width ?? 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

export const NO_ZOOM_SETTINGS = { enableSelectionZooming: false };

// Flip to re-enable drag-to-zoom; nothing else needs changing. Mouse-wheel zoom
// stays off even then — it captures the wheel and stops the page scrolling.
const MINUTE_ZOOM_ENABLED = false;

export const MINUTE_ZOOM_SETTINGS = MINUTE_ZOOM_ENABLED
  ? {
      enableSelectionZooming: true,
      enablePinchZooming: true,
      enableMouseWheelZooming: false,
      mode: "X",
      toolbarItems: ["ZoomIn", "ZoomOut", "Pan", "Reset"],
    }
  : NO_ZOOM_SETTINGS;

export function minuteCrosshair(tickColor) {
  return {
    enable: true,
    lineType: "Vertical",
    dashArray: "4,3",
    line: { width: 1, color: tickColor },
  };
}

export const NO_CROSSHAIR = { enable: false };

// For a chart that already knows its range. `ref` goes on the element whose
// width sets the label density; `axisProps` is spread into primaryXAxis. Pass
// null to leave the axis alone.
export function useMinuteAxisFor(range, tickColor) {
  const ref = useRef(null);
  const width = useElementWidth(ref);

  const min = range?.min ?? null;
  const max = range?.max ?? null;

  // Kept here, not in the chart: a new label step re-renders the axis, and a
  // re-render without these would snap the view back out to the full range.
  const [zoom, setZoom] = useState({ factor: 1, position: 0 });

  useEffect(() => {
    setZoom((z) => (z.factor === 1 && z.position === 0 ? z : { factor: 1, position: 0 }));
  }, [min, max]);

  const onZoomComplete = useCallback((args) => {
    if (args.axis?.name !== "primaryXAxis") return;
    const factor = args.currentZoomFactor;
    const position = args.currentZoomPosition;
    setZoom((z) =>
      z.factor === factor && z.position === position ? z : { factor, position }
    );
  }, []);

  const step = useMemo(() => {
    if (min == null) return 60;
    const span = (max - min) * zoom.factor;
    return pickMinuteStep(span, width - PLOT_GUTTER_PX);
  }, [min, max, zoom.factor, width]);

  const axisProps = useMemo(() => {
    if (min == null) return null;
    return {
      valueType: "Double",
      minimum: min,
      maximum: max,
      interval: step,
      rangePadding: "None",
      majorGridLines: { width: 0 },
      majorTickLines: { width: 1, height: 7, color: tickColor },
      minorTicksPerInterval: minorTicksFor(step),
      minorTickLines: { width: 1, height: 4, color: tickColor },
      minorGridLines: { width: 0 },
      zoomFactor: zoom.factor,
      zoomPosition: zoom.position,
      // Left on, the chart substitutes its own interval and undoes the step.
      enableAutoIntervalOnZooming: false,
      labelIntersectAction: "Rotate45",
    };
  }, [min, max, step, zoom, tickColor]);

  return { ref, range, step, zoom, onZoomComplete, axisProps };
}

export function useMinuteAxis({ series, mode, tickColor }) {
  const range = useMemo(() => minuteRangeOf(series, mode), [series, mode]);
  return useMinuteAxisFor(range, tickColor);
}
